import {
  FilterMenuBase,
  FilterMenuSection,
} from '@/components/FilterMenu/FilterMenuBase'
import { OrgPicker } from '@/components/OrgPicker/OrgPicker'
import { Box } from '@chakra-ui/react'
import { type KeyboardEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isOrgIdShaped } from './orgFilterState'

type OrgFilterMenuProps = {
  buttonLabel: string
  selectedOrgId: string | null
  onApply: (orgId: string) => void
  inputId: string
}

const COMBOBOX_INPUT = '[data-scope="combobox"][data-part="input"]'

function isOptionListOpen(container: HTMLElement) {
  return (
    container.querySelector(COMBOBOX_INPUT)?.getAttribute('aria-expanded') ===
    'true'
  )
}

function keepKeysInPicker(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key !== 'Escape' || isOptionListOpen(event.currentTarget)) {
    event.stopPropagation()
  }
}

export function OrgFilterMenu({
  buttonLabel,
  selectedOrgId,
  onApply,
  inputId,
}: Readonly<OrgFilterMenuProps>) {
  const [contentKey, setContentKey] = useState(0)

  const onMenuOpen = useCallback(() => {
    setTimeout(() => document.getElementById(inputId)?.focus(), 10)
  }, [inputId])

  const onMenuClose = useCallback(() => setContentKey((key) => key + 1), [])

  return (
    <FilterMenuBase
      buttonLabel={buttonLabel}
      hasSelectedValue={selectedOrgId !== null}
      menuWidth={400}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
    >
      {({ closeMenu }) => (
        <OrgFilterMenuContent
          key={contentKey}
          buttonLabel={buttonLabel}
          inputId={inputId}
          onApply={onApply}
          closeMenu={closeMenu}
        />
      )}
    </FilterMenuBase>
  )
}

type OrgFilterMenuContentProps = {
  buttonLabel: string
  inputId: string
  onApply: (orgId: string) => void
  closeMenu: () => void
}

function OrgFilterMenuContent({
  buttonLabel,
  inputId,
  onApply,
  closeMenu,
}: Readonly<OrgFilterMenuContentProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const applyAndClose = (orgId: string) => {
    onApply(orgId)
    closeMenu()
  }

  return (
    <FilterMenuSection>
      <Box onKeyDown={keepKeysInPicker}>
        <OrgPicker
          id={inputId}
          label={buttonLabel}
          value=""
          helperText={String(t('orgFilter.helper'))}
          onChange={(orgId) => {
            if (orgId !== '') {
              applyAndClose(orgId)
            }
          }}
          onTextSubmit={(text) => {
            if (isOrgIdShaped(text)) {
              applyAndClose(text)
            }
          }}
        />
      </Box>
    </FilterMenuSection>
  )
}
